import { useMutation, useQuery } from "@apollo/react-hooks";
import { useEffect, useState } from "react";
import {
  CONSULTORY_PARAMETERS,
  CREATE_OR_UPDATE_PARAMETERS,
  GET_FREQUENCIES,
  GET_SPECIALITIES_BY_MEDICAL,
  MEDICAL_PARAMETERS_TO_GROUPS,
} from "./queries";
import { differenceBy, isEmpty } from "lodash";
import { useToasts } from "react-toast-notifications";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

const INITIAL_STATE = {
  data: [],
  loading: false,
  modalIsOpenParameters: false,
  optionsSpecialitiesByMedical: [],
  optionsFrequencies: [],
  selectedSpecialty: {},
  optionParametersGroupBySpecialitySelected: [],
  selectedParameterGroup: {},
  dataMedicalParametersToGroups: [],
  activeTab: null,
  dataParameterByGroup: [],
  dataConsultoryParametersAdded: [],
  dataMedicalParametersToGroupsExcludes: [],
  onActionButtonAdd: false,
};

const relations = {
  1: [
    "speciality.specialtiesMedicalParametersGroup",
    "speciality.specialtiesMedicalParametersGroup.medicalParameterGroup",
  ],
  2: [
    "medicalParameter",
    "medicalParameter.gender",
    "medicalParameter.type",
    "medicalParameter.frequency",
  ],
  3: [
    "specialty",
    "medicalParameterGroup",
    "consultoryParametersDetail",
    "consultoryParametersDetail.frequency",
    "consultoryParametersDetail.medicalParameters",
    "consultoryParametersDetail.medicalParameters.gender",
    "consultoryParametersDetail.medicalParameters.type",
    "consultoryParametersDetail.medicalParameters.frequency",
  ],
};

const sortDetailsByGroupOrder = (details = [], groupParameters = []) => {
  const positions = new Map(
    groupParameters.map((item, index) => [item?.medicalParameter?.id, index]),
  );

  return [...details].sort(
    (first, second) =>
      (positions.get(first?.medicalParameter?.id) ?? Number.MAX_SAFE_INTEGER) -
      (positions.get(second?.medicalParameter?.id) ?? Number.MAX_SAFE_INTEGER),
  );
};

export default function useActions(
  { medicalStaffId, branchOfficeId, centerId: pharmacyProviderId },
  history,
) {
  const [state, setState] = useState(INITIAL_STATE);
  const { addToast } = useToasts();
  const { params } = useParams();

  console.log("MedicalParametersAddOrModify state", state);

  const {
    data: { specialitiesByMedical } = {},
    loading: specialitiesByMedicalLoading,
  } = useQuery(GET_SPECIALITIES_BY_MEDICAL, {
    skip: !!params,
    fetchPolicy: "network-only",
    variables: {
      relations: ["speciality", ...relations[1]],
      where: { medicalStaffId },
    },
  });

  const { data: { frequencies } = {}, loading: frequenciesLoading } = useQuery(
    GET_FREQUENCIES,
    {
      fetchPolicy: "network-only",
    },
  );

  const {
    data: { consultoryParameters } = {},
    loading: consultoryParametersLoading,
  } = useQuery(CONSULTORY_PARAMETERS, {
    ...(!params ? { skip: !state.selectedSpecialty?.id } : {}),
    fetchPolicy: "network-only",
    variables: {
      relations: relations[3],
      where: {
        id: parseInt(params) || null,
        medicalStaffId,
        branchOfficeId,
        ...(!params
          ? {
              specialtiesId: state.selectedSpecialty?.id,
            }
          : {}),
      },
    },
  });
  const [medicalParametersCreateOrUpdate] = useMutation(
    CREATE_OR_UPDATE_PARAMETERS,
  );

  const {
    data: { medicalParametersToGroups } = {},
    loading: medicalParametersToGroupsLoading,
  } = useQuery(MEDICAL_PARAMETERS_TO_GROUPS, {
    skip:
      (!state.onActionButtonAdd && !params) ||
      (isEmpty(state.selectedParameterGroup) && !params),
    fetchPolicy: "network-only",
    variables: {
      relations: relations[2],
      where: {
        medicalParameterGroupId: state.selectedParameterGroup?.id || null,
      },
    },
  });

  console.log("state of component MedicalParametersAddOrModify", state);

  const onChange = (key, value) => {
    setState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onChangeFrequencyAndStatus = (
    key,
    value,
    parameterGroupId,
    medicalParameterId,
  ) => {
    if (key === "enabled" && !value && !!params) {
      if (
        state.dataMedicalParametersToGroups[0].consultoryParametersDetail?.filter(
          (item) => item.enabled,
        )?.length === 1
      )
        return addToast("Debe conservar al menos 1 parametro activo!!", {
          appearance: "error",
          autoDismiss: true,
        });
    }

    setState((prev) => ({
      ...prev,
      dataMedicalParametersToGroups: prev.dataMedicalParametersToGroups.map(
        (item) => {
          if (item.medicalParameterGroup.id === parameterGroupId) {
            const consultoryParametersDetail =
              item?.consultoryParametersDetail?.map((item2) => {
                if (item2.medicalParameter.id === medicalParameterId) {
                  return {
                    ...item2,
                    [key]: value,
                  };
                }
                return item2;
              });
            return { ...item, consultoryParametersDetail };
          }
          return item;
        },
      ),
    }));
  };

  const toggle = () => {
    onChange("modalIsOpenParameters", false);
  };

  const PAIRED_PARAMETERS = {
    4: {
      groupName: "Indice Antropométricos (Adulto)",
      pairs: [
        { id: 19, name: "Peso (Adulto)" },
        { id: 17, name: "Estatura (Adulto)" },
      ],
    },
    5: {
      groupName: "Indice Antropométricos (Infantil)",
      pairs: [
        { id: 20, name: "Peso (Infantil)" },
        { id: 18, name: "Estatura (Infantil)" },
      ],
    },
  };

  const validatePairedParameters = (dataMedicalParametersToGroups) => {
    for (const group of dataMedicalParametersToGroups) {
      const groupId = group.medicalParameterGroup?.id;
      const pairedConfig = PAIRED_PARAMETERS[groupId];

      if (!pairedConfig) continue;

      const enabledDetails = group.consultoryParametersDetail.filter(
        (d) => d.enabled,
      );
      const enabledParamIds = enabledDetails.map(
        (d) => d.medicalParameter?.id,
      );

      const pairedIds = pairedConfig.pairs.map((p) => p.id);
      const activePairs = pairedIds.filter((id) =>
        enabledParamIds.includes(id),
      );

      if (activePairs.length > 0 && activePairs.length < pairedIds.length) {
        const missingParams = pairedConfig.pairs
          .filter((p) => !enabledParamIds.includes(p.id))
          .map((p) => p.name);
        const activeParams = pairedConfig.pairs
          .filter((p) => enabledParamIds.includes(p.id))
          .map((p) => p.name);

        const message =
          `En el grupo "${pairedConfig.groupName}", los parámetros ${pairedConfig.pairs.map((p) => `"${p.name}"`).join(" y ")} deben ser ingresados juntos. ` +
          `Tiene activo: ${activeParams.join(", ")}. Falta: ${missingParams.join(", ")}.`;

        addToast(message, { appearance: "error", autoDismiss: true });
        throw new Error("Parámetros pares requeridos");
      }
    }
  };

  const validateFrequencies = (consultoryParametersDetail) => {
    for (const item of consultoryParametersDetail) {
      if (!item?.id && item?.enabled && isEmpty(item?.frequency)) {
        addToast(
          "Debe Seleccionar La Frecuencia de los Parametros Activos. Verifique...",
          { appearance: "error", autoDismiss: true },
        );
        throw new Error("Frecuencia requerida");
      }
    }
  };

  const buildDetailPayload = (consultoryParametersDetail) => {
    return consultoryParametersDetail
      .map((item) => {
        const isNewAndEnabled = !item.id && item.enabled;
        const isExistingInEditMode = !!params && item?.id;

        if (isNewAndEnabled || isExistingInEditMode) {
          return {
            id: item?.id,
            frequency: item.frequency,
            medicalParametersId: item?.medicalParameter?.id,
            enabled: item.enabled,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const buildPayload = (dataMedicalParametersToGroups) => {
    return dataMedicalParametersToGroups
      .filter(
        (group) =>
          !!params ||
          group.consultoryParametersDetail.some((d) => !d.id && d.enabled),
      )
      .map(
        ({
          medicalParameterGroup: { id, name },
          consultoryParametersDetail,
          specialty,
          id: medicalGroupId,
        }) => {
          validateFrequencies(consultoryParametersDetail);

          return {
            id: medicalGroupId,
            branchOfficeId,
            medicalStaffId,
            pharmacyProviderId,
            specialty,
            amountConfiguration: consultoryParametersDetail.filter(
              ({ enabled }) => enabled,
            ).length,
            medicalParameterGroup: { id, name },
            consultoryParametersDetail:
              buildDetailPayload(consultoryParametersDetail),
          };
        },
      );
  };

  const createOrUpdateParameters = async () => {
    try {
      onChange("loading", true);
      const { dataMedicalParametersToGroups } = state;

      validatePairedParameters(dataMedicalParametersToGroups);

      const payload = buildPayload(dataMedicalParametersToGroups);

      if (!isEmpty(payload)) {
        await medicalParametersCreateOrUpdate({
          variables: {
            data: payload || [],
          },
        });
        addToast(
          !params ? "Creado de Manera Exitosa" : "Modificacion Exitosa",
          {
            appearance: "success",
            autoDismiss: true,
          },
        );
      }
      history.goBack();
    } catch (error) {
      addToast(error.message, { appearance: "error", autoDismiss: true });
      console.log(error.message);
    } finally {
      onChange("loading", false);
    }
  };

  useEffect(() => {
    if (specialitiesByMedical && !specialitiesByMedicalLoading)
      setState((prev) => ({
        ...prev,
        optionsSpecialitiesByMedical:
          specialitiesByMedical
            ?.map((item) => item?.speciality)
            .filter(
              (item) => !isEmpty(item?.specialtiesMedicalParametersGroup),
            ) || [],
      }));
  }, [specialitiesByMedical, specialitiesByMedicalLoading]);

  useEffect(() => {
    if (consultoryParameters && !consultoryParametersLoading) {
      //dataMedicalParametersToGroupsExcludes se va a utilizar para excluir los grupos que han sido agregado en la BD
      if (!params)
        return setState((prev) => ({
          ...prev,
          dataMedicalParametersToGroupsExcludes: consultoryParameters,
        }));

      const consParameters = consultoryParameters[0] ?? {};
      setState((prev) => ({
        ...prev,
        selectedParameterGroup: consParameters?.medicalParameterGroup,
        selectedSpecialty: consParameters?.specialty,
        dataMedicalParametersToGroups: [
          {
            id: consParameters?.id,
            medicalParameterGroup: consParameters?.medicalParameterGroup,
            name: consParameters?.medicalParameterGroup?.name,
            consultoryParametersDetail:
              consParameters?.consultoryParametersDetail?.map(
                ({ enabled, frequency, medicalParameters, id } = {}) => ({
                  id,
                  frequency,
                  medicalParameter: medicalParameters,
                  enabled,
                  specialty: consParameters?.specialty,
                  medicalParameterGroup: consParameters?.medicalParameterGroup,
                }),
              ),
          },
        ],
      }));
    }
  }, [consultoryParameters, consultoryParametersLoading, params]);

  useEffect(() => {
    if (frequencies && !frequenciesLoading)
      setState((prev) => ({
        ...prev,
        optionsFrequencies: frequencies,
      }));
  }, [frequencies, frequenciesLoading]);

  useEffect(() => {
    if (
      medicalParametersToGroups &&
      !medicalParametersToGroupsLoading &&
      !isEmpty(state.selectedParameterGroup) &&
      !!params
    ) {
      if (state.activeTab) return;
      const medicalParametersToGroupsInclude = differenceBy(
        medicalParametersToGroups,
        state.dataMedicalParametersToGroups[0].consultoryParametersDetail,
        (item) => item.medicalParameter.id,
      );
      return setState((prev) => ({
        ...prev,
        activeTab: state.selectedParameterGroup.id,
        dataMedicalParametersToGroups: [
          {
            ...prev.dataMedicalParametersToGroups[0],
            consultoryParametersDetail: sortDetailsByGroupOrder(
              [
                ...prev.dataMedicalParametersToGroups[0]
                  .consultoryParametersDetail,
                ...medicalParametersToGroupsInclude?.map(
                  (item) =>
                    ({
                      ...item,
                      enabled: false,
                      frequency: item?.medicalParameter?.frequency ?? null,
                      medicalParameterGroup: state.selectedParameterGroup,
                    }) ?? [],
                ),
              ],
              medicalParametersToGroups,
            ),
          },
        ],
      }));
    }
  }, [
    medicalParametersToGroups,
    medicalParametersToGroupsLoading,
    params,
    state.activeTab,
    state.dataMedicalParametersToGroups,
    state.selectedParameterGroup,
  ]);

  useEffect(() => {
    if (
      medicalParametersToGroups &&
      !medicalParametersToGroupsLoading &&
      !isEmpty(state.selectedParameterGroup) &&
      !params
    ) {
      const consultoryParametersDetail =
        medicalParametersToGroups?.map((item) => ({
          enabled: true,
          frequency: item?.medicalParameter?.frequency ?? null,
          medicalParameterGroup: state?.selectedParameterGroup,
          medicalParameter: item?.medicalParameter,
        })) || [];
      setState((prev) => ({
        ...prev,
        selectedParameterGroup: {},
        onActionButtonAdd: false,
        activeTab: prev.selectedParameterGroup.id,
        dataMedicalParametersToGroups: [
          ...prev.dataMedicalParametersToGroups,
          {
            specialty: state.selectedSpecialty,
            medicalParameterGroup: { ...prev.selectedParameterGroup },
            consultoryParametersDetail,
          },
        ],
      }));
    }
  }, [
    medicalParametersCreateOrUpdate,
    medicalParametersToGroups,
    medicalParametersToGroupsLoading,
    params,
    state.dataMedicalParametersToGroups,
    state.selectedParameterGroup,
    state.selectedSpecialty,
  ]);

  return [
    { state },
    {
      onChange,
      toggle,
      createOrUpdateParameters,
      params,
      onChangeFrequencyAndStatus,
      tableLoading:
        consultoryParametersLoading || medicalParametersToGroupsLoading,
    },
  ];
}
